/**
 * The Dock window (spec 2026-09-22 §4, §5): the desktop shell hosts the web
 * Dock, with the Line on, in its own floating panel at /dock. Everything here is
 * inert outside that panel: on the web and in the shell's main window every
 * predicate is false, so today's bytes hold.
 */
import { destinationOfPath, type RouterAction } from "@bash/shared";
import { isDesktopShell } from "@/lib/desktop-shell";

/** Build-time flag (NEXT_PUBLIC_* is inlined); read at call time so tests can stub it. */
export function isDockHostEnabled(): boolean {
  return process.env.NEXT_PUBLIC_DOCK_HOST_V1 === "true";
}

/** True only inside the shell's dock panel: the shell marker, the route, the flag. */
export function isDockWindow(win: Window = window): boolean {
  return (
    isDockHostEnabled() &&
    isDesktopShell(win) &&
    win.location.pathname === "/dock"
  );
}

/** The wire the shell's panel speaks (spec §4). Verbatim strings, both sides. */
export const DOCK_ACTION_EVENT = "vestra://dock-action";
export const DOCK_SUMMON_EVENT = "vestra://dock-summon";
export type DockSummonMode = "talk" | "type" | "shelf";

/** The notch-parity events (contract 2026-09-23). Each carries the WHOLE
 *  current state, never a delta, so the last one heard is the truth. */
export const DOCK_TASKS_EVENT = "vestra://dock-tasks";
export const DOCK_MOMENT_EVENT = "vestra://dock-moment";
export const DOCK_STATUS_EVENT = "vestra://dock-status";
/** The panel's listeners are in: the shell re-emits all three at once, so a
 *  page that loads after `dock::show` never misses the first state. */
export const DOCK_READY_EVENT = "vestra://dock-ready";

export type DockTaskState = "working" | "needs" | "done" | "failed";
export interface DockTask {
  id: string;
  title: string;
  state: DockTaskState;
  /** A done task's answer (the notch's bloom); absent otherwise. */
  answer?: string;
}
export interface DockMoment {
  id: string;
  kind: "noticing" | "update";
  title: string;
  body: string;
  acceptLabel: string;
  dismissLabel: string;
}
export interface DockStatus {
  runner: "online" | "starting" | "offline";
  signedIn: boolean;
}

interface TauriEventApi {
  emit: (event: string, payload?: unknown) => Promise<void>;
  listen: (
    event: string,
    handler: (e: { payload: unknown }) => void
  ) => Promise<() => void>;
}
interface TauriCoreApi {
  invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
}
type TauriGlobal = { core?: TauriCoreApi; event?: TauriEventApi };

function tauri(win: Window = window): TauriGlobal | null {
  const t = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
  return t ?? null;
}

/**
 * The shell's own answer: is its floating Dock window on? The build flag is
 * on for a whole environment, but the panel exists only when the shell's flag
 * (its marker file, read once per launch: dock.rs `enabled()`) is on too, so
 * only the shell can say. Asked once per page, never again: the shell's answer
 * cannot change until the next launch. Null until it answers.
 */
let shellDockOn: boolean | null = null;
let shellAsked = false;
const shellListeners = new Set<() => void>();

function askShell(win: Window): void {
  if (shellAsked) return;
  if (!isDockHostEnabled() || !isDesktopShell(win) || isDockWindow(win)) return;
  const invoke = tauri(win)?.core?.invoke;
  if (!invoke) return;
  shellAsked = true;
  void invoke("dock_flag_state")
    .then((on) => {
      shellDockOn = on === true;
      shellListeners.forEach((l) => l());
    })
    // A shell that has not granted the command keeps the main window's own
    // composer: the old behaviour, never a window with none.
    .catch((err: unknown) =>
      console.error("[dock-window] flag state failed", err)
    );
}

/**
 * True only in the shell's MAIN window while the floating Dock is on: the
 * panel is the composer then, and the main window draws none of its own (one
 * face, founder law). False on the web, in the panel itself, with the shell's
 * flag off, and until the shell has answered.
 */
export function floatingDockOn(win: Window = window): boolean {
  return (
    shellDockOn === true &&
    isDockHostEnabled() &&
    isDesktopShell(win) &&
    !isDockWindow(win)
  );
}

/** Subscribe to the shell's answer; the first subscriber asks the shell. */
export function subscribeFloatingDock(cb: () => void): () => void {
  shellListeners.add(cb);
  askShell(window);
  return () => {
    shellListeners.delete(cb);
  };
}

/** Tests only: forget the shell's answer so the next subscriber asks again. */
export function resetFloatingDockForTests(): void {
  shellDockOn = null;
  shellAsked = false;
  shellListeners.clear();
}

/** The panel's navigation goes to the main window (spec §4): one event, then nothing else here. */
export function forwardAction(
  action: RouterAction,
  win: Window = window
): boolean {
  if (!isDockWindow(win)) return false;
  const emit = tauri(win)?.event?.emit;
  if (!emit) return false;
  void emit(DOCK_ACTION_EVENT, { action }).catch((err: unknown) =>
    console.error("[dock-window] forward failed", err)
  );
  return true;
}

/**
 * A route the panel would have pushed goes to the main window instead, as the
 * destination's own app_control (the main window's belt reads the route).
 * False outside the panel, where the caller's own router keeps it.
 */
export function forwardRoute(route: string, win: Window = window): boolean {
  if (!isDockWindow(win)) return false;
  const d = destinationOfPath(route);
  return forwardAction(
    {
      kind: "app_control",
      view: (d?.id ?? "home") as Extract<
        RouterAction,
        { kind: "app_control" }
      >["view"],
      route,
      label: d?.label ?? "",
    },
    win
  );
}

/**
 * The main window's side: perform what the panel forwarded. Inert on the web.
 *
 * Only the MAIN window may listen. The panel is in the same shell and would
 * hear its own forward, so it would move itself instead of the window the
 * person is looking at. With the flag off there is no panel to forward
 * anything, and the main window must not change by a single call, so the
 * subscription is gated on the flag too: nothing is installed.
 */
export function onDockAction(
  cb: (action: RouterAction) => void,
  win: Window = window
): () => void {
  if (!isDockHostEnabled() || !isDesktopShell(win) || isDockWindow(win)) {
    return () => {};
  }
  const listen = tauri(win)?.event?.listen;
  if (!listen) return () => {};
  let off: (() => void) | null = null;
  let gone = false;
  void listen(DOCK_ACTION_EVENT, (e) => {
    const p = e.payload as { action?: RouterAction } | null;
    if (p?.action) cb(p.action);
  })
    .then((unlisten) => {
      if (gone) unlisten();
      else off = unlisten;
    })
    // A shell that has not granted the listen capability must say so once, not
    // throw an unhandled rejection into the main window.
    .catch((err: unknown) => console.error("[dock-window] listen failed", err));
  return () => {
    gone = true;
    off?.();
  };
}

/**
 * One shell event, heard in the panel alone; the payload is parsed by the
 * caller. `ready` settles true once the listener is in (false when none was
 * installed), which is what dock-ready waits on.
 */
function listening(
  event: string,
  cb: (payload: unknown) => void,
  win: Window
): { off: () => void; ready: Promise<boolean> } {
  const none = { off: () => {}, ready: Promise.resolve(false) };
  if (!isDockWindow(win)) return none;
  const listen = tauri(win)?.event?.listen;
  if (!listen) return none;
  let off: (() => void) | null = null;
  let gone = false;
  const ready = listen(event, (e) => cb(e.payload))
    .then((unlisten) => {
      if (gone) unlisten();
      else off = unlisten;
      return true;
    })
    .catch((err: unknown) => {
      console.error("[dock-window] listen failed", err);
      return false;
    });
  return {
    off: () => {
      gone = true;
      off?.();
    },
    ready,
  };
}

function listenInDock(
  event: string,
  cb: (payload: unknown) => void,
  win: Window
): () => void {
  return listening(event, cb, win).off;
}

/** The shell's summon (hold Control, tap Option) lands here in the panel. */
export function onDockSummon(
  cb: (mode: DockSummonMode) => void,
  win: Window = window
): () => void {
  return listenInDock(
    DOCK_SUMMON_EVENT,
    (p) => {
      const mode = (p as { mode?: string } | null)?.mode;
      if (mode === "talk" || mode === "type" || mode === "shelf") cb(mode);
    },
    win
  );
}

const TASK_STATES: ReadonlySet<string> = new Set([
  "working",
  "needs",
  "done",
  "failed",
]);

/** The tasks the notch's dots showed, newest first. A row that cannot be read is dropped. */
const tasksOf =
  (cb: (tasks: DockTask[]) => void) =>
  (p: unknown): void => {
    const raw = (p as { tasks?: unknown } | null)?.tasks;
    if (!Array.isArray(raw)) return;
    const out: DockTask[] = [];
    for (const t of raw) {
      if (
        typeof t?.id !== "string" ||
        typeof t?.title !== "string" ||
        !TASK_STATES.has(t?.state)
      )
        continue;
      out.push({
        id: t.id,
        title: t.title,
        state: t.state,
        ...(typeof t.answer === "string" && t.answer
          ? { answer: t.answer }
          : {}),
      });
    }
    cb(out);
  };

/** A moment the notch would have drawn (a noticing offer, the update prompt); null clears it. */
const momentOf =
  (cb: (moment: DockMoment | null) => void) =>
  (p: unknown): void => {
    const m = (p as { moment?: DockMoment | null } | null)?.moment;
    if (m === null) cb(null);
    else if (m && typeof m.id === "string") cb(m);
  };

/** The Mac's runner and the session, as the notch's wings showed them. */
const statusOf =
  (cb: (status: DockStatus) => void) =>
  (p: unknown): void => {
    const s = p as Partial<DockStatus> | null;
    if (
      (s?.runner === "online" ||
        s?.runner === "starting" ||
        s?.runner === "offline") &&
      typeof s.signedIn === "boolean"
    )
      cb({ runner: s.runner, signedIn: s.signedIn });
  };

export function onDockTasks(
  cb: (tasks: DockTask[]) => void,
  win: Window = window
): () => void {
  return listenInDock(DOCK_TASKS_EVENT, tasksOf(cb), win);
}

export function onDockMoment(
  cb: (moment: DockMoment | null) => void,
  win: Window = window
): () => void {
  return listenInDock(DOCK_MOMENT_EVENT, momentOf(cb), win);
}

export function onDockStatus(
  cb: (status: DockStatus) => void,
  win: Window = window
): () => void {
  return listenInDock(DOCK_STATUS_EVENT, statusOf(cb), win);
}

/**
 * All three at once, then dock-ready: the shell re-emits the whole state on
 * it, so the first tasks, moment and status reach a page however late it
 * loaded. Said only once every listener is in, never on a partial set.
 */
export function subscribeDockShell(
  h: {
    onTasks: (tasks: DockTask[]) => void;
    onMoment: (moment: DockMoment | null) => void;
    onStatus: (status: DockStatus) => void;
  },
  win: Window = window
): () => void {
  const subs = [
    listening(DOCK_TASKS_EVENT, tasksOf(h.onTasks), win),
    listening(DOCK_MOMENT_EVENT, momentOf(h.onMoment), win),
    listening(DOCK_STATUS_EVENT, statusOf(h.onStatus), win),
  ];
  let gone = false;
  void Promise.all(subs.map((s) => s.ready)).then((ok) => {
    if (gone || !ok.every(Boolean)) return;
    void tauri(win)
      ?.event?.emit(DOCK_READY_EVENT, {})
      .catch((err: unknown) =>
        console.error("[dock-window] ready failed", err)
      );
  });
  return () => {
    gone = true;
    subs.forEach((s) => s.off());
  };
}

/** A turn from the Dock started a task: the shell's feed tracks it as it did the notch's. Idempotent there. */
export function trackDockTask(
  t: { taskId: string; title: string },
  win: Window = window
): void {
  if (!isDockWindow(win)) return;
  void tauri(win)
    ?.core?.invoke("dock_track_task", { taskId: t.taskId, title: t.title })
    .catch((err: unknown) => console.error("[dock-window] track failed", err));
}

/**
 * The Line is live (connecting or open) or not: the shell keeps the panel in
 * screen recordings exactly while a call is live. Level, not edge: the
 * caller sends the current value on mount and on every change. An older
 * shell without the command rejects, which changes nothing.
 */
export function setDockLineLive(live: boolean, win: Window = window): void {
  if (!isDockWindow(win)) return;
  void tauri(win)
    ?.core?.invoke("dock_set_line_live", { live })
    .catch(() => {});
}

/**
 * The person answered a moment. Resolves to the offer's prompt when an
 * accepted noticing offer is to be asked (the caller posts it), else null.
 */
export async function actOnDockMoment(
  id: string,
  accept: boolean,
  win: Window = window
): Promise<string | null> {
  if (!isDockWindow(win)) return null;
  const invoke = tauri(win)?.core?.invoke;
  if (!invoke) return null;
  try {
    const out = await invoke("dock_moment_act", { id, accept });
    return typeof out === "string" && out.trim() ? out : null;
  } catch (err) {
    console.error("[dock-window] moment failed", err);
    return null;
  }
}

/** The page's interactive rect in CSS pixels of the panel's viewport; the shell grants clicks inside it. */
export function reportDockRect(
  r: { x: number; y: number; w: number; h: number },
  win: Window = window
): void {
  if (!isDockWindow(win)) return;
  void tauri(win)
    ?.core?.invoke("dock_report_rect", { x: r.x, y: r.y, w: r.w, h: r.h })
    .catch(() => {});
}

/** A drag on the ball moves the whole panel (spec §4). */
export function beginDockDrag(win: Window = window): void {
  if (!isDockWindow(win)) return;
  void tauri(win)
    ?.core?.invoke("plugin:window|start_dragging", undefined)
    .catch((err: unknown) => console.error("[dock-window] drag failed", err));
}

/** Escape or a fold: hand the keyboard back to the app the person was in. */
export function releaseDockFocus(win: Window = window): void {
  if (!isDockWindow(win)) return;
  void tauri(win)
    ?.core?.invoke("dock_release_focus")
    .catch(() => {});
}
